import { renderMermaid } from "@lib/mermaid";
import CheckIcon from "@mui/icons-material/CheckRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import FullscreenIcon from "@mui/icons-material/FullscreenRounded";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { FullscreenDiagram } from "./FullscreenDiagram";

interface MermaidDiagramProps {
  code: string;
}

/**
 * Trigger a browser download of `bytes` as a file named `filename`.
 *
 * Pure DOM — no React, no third-party libs. Used by the SVG / PNG
 * download buttons; isolated as a helper so the component body stays
 * readable.
 */
function _downloadBlob(bytes: Blob, filename: string) {
  const url = URL.createObjectURL(bytes);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Some browsers refuse to fire `click()` on an anchor that isn't in
  // the document. Append-then-remove keeps the page tree clean.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Free the blob URL on the next tick — Chrome leaks if you revoke
  // immediately during a download click.
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

/**
 * Rasterise an inline SVG element to a PNG Blob via the Canvas API.
 *
 * Native browser path — no canvas / svg2png / dom-to-image dependency.
 * Resolves with the PNG bytes. Rejects if the browser refuses to load
 * the SVG (rare; usually only happens if the SVG references external
 * resources we'd block via CSP).
 *
 * The 2× scale factor produces sharper PNGs at any zoom level. Most
 * users open the PNG in Slack / Notion / Figma where the source SVG
 * gets rasterised again, so a higher source resolution avoids visible
 * blur.
 */
async function _svgElementToPngBlob(svg: SVGSVGElement): Promise<Blob> {
  // Serialise + base64-encode so the <img> below can data: load it
  // without the network. Wrap in btoa via UTF-8-safe encoder.
  const xml = new XMLSerializer().serializeToString(svg);
  const dataUrl =
    "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));

  // Use the SVG's bbox for sizing — the element's clientWidth/Height
  // can be 0 if the SVG was already detached, so prefer the explicit
  // viewBox or width/height attributes.
  const widthAttr = svg.getAttribute("width");
  const heightAttr = svg.getAttribute("height");
  const vb = svg.viewBox.baseVal;
  const baseWidth =
    (widthAttr ? Number.parseFloat(widthAttr) : vb.width) ||
    svg.clientWidth ||
    800;
  const baseHeight =
    (heightAttr ? Number.parseFloat(heightAttr) : vb.height) ||
    svg.clientHeight ||
    600;

  const SCALE = 2;
  const w = Math.max(1, Math.round(baseWidth * SCALE));
  const h = Math.max(1, Math.round(baseHeight * SCALE));

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Browser refused to load the SVG."));
    img.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context not available.");
  ctx.drawImage(img, 0, 0, w, h);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("toBlob returned null.")),
      "image/png",
    );
  });
}

export function MermaidDiagram({ code }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef<string>("");
  const [rendering, setRendering] = useState(true);
  // Brief "✓ copied" affordance after the copy button fires; reverts to
  // the clipboard icon after 1.5 s.
  const [copied, setCopied] = useState(false);
  // Captured AFTER renderMermaid + DOMPurify have run, so it's safe to
  // re-inject into the FullscreenDiagram modal via dangerouslySetInner
  // HTML. Empty string until the first successful render.
  const [svgMarkup, setSvgMarkup] = useState("");
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  useEffect(() => {
    if (!containerRef.current || renderedRef.current === code) return;
    renderedRef.current = code;

    const el = containerRef.current;
    el.innerHTML = "";
    setRendering(true);
    setSvgMarkup("");

    void renderMermaid(code, el).finally(() => {
      setRendering(false);
      // Capture the (already-sanitised) SVG so the fullscreen modal can
      // re-render it without re-running mermaid. Reading after the
      // render promise settles guarantees the container has the final
      // SVG even if a subsequent unrelated re-render races.
      setSvgMarkup(el.innerHTML);
    });
  }, [code]);

  const _handleCopySource = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail under strict permissions; fall back to
      // a transient prompt the user can copy from. Rare in practice
      // (chat is on https / localhost which both grant clipboard).
      window.prompt("Copy mermaid source:", code);
    }
  };

  const _handleDownloadSvg = () => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    // Re-stamp xmlns so the file opens cleanly outside the browser.
    const xmlWithNs = xml.includes("xmlns=")
      ? xml
      : xml.replace("<svg ", '<svg xmlns="http://www.w3.org/2000/svg" ');
    const blob = new Blob([xmlWithNs], { type: "image/svg+xml" });
    _downloadBlob(blob, `circuit-diagram-${Date.now()}.svg`);
  };

  const _handleDownloadPng = async () => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;
    try {
      const blob = await _svgElementToPngBlob(svg);
      _downloadBlob(blob, `circuit-diagram-${Date.now()}.png`);
    } catch (err) {
      // PNG export is best-effort; if the browser refuses (rare), fall
      // back to the SVG path so the user still gets *something*.
      console.warn("PNG export failed, falling back to SVG:", err);
      _handleDownloadSvg();
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "surface.raised",
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: 2,
        p: 2,
        my: 1.25,
        // Hard containment — even if Mermaid emits something with an
        // exploding intrinsic size, it cannot push the page layout.
        overflow: "hidden",
        maxWidth: "100%",
        contain: "layout paint",
        minHeight: 80,
        "& svg": {
          maxWidth: "100%",
          height: "auto",
          display: "block",
        },
        // Toolbar stays hidden until the user moves over the diagram.
        // Touch users see it on tap (most touch UAs treat tap as
        // hover-then-click).
        "&:hover .mermaid-toolbar, &:focus-within .mermaid-toolbar": {
          opacity: 1,
        },
      }}
    >
      {rendering && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={22} />
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{ display: "flex", justifyContent: "center" }}
      />

      {/* Toolbar — copy source, download SVG, download PNG. */}
      <Box
        className="mermaid-toolbar"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 0.5,
          padding: "4px",
          borderRadius: 1.5,
          bgcolor: "rgba(10, 16, 34, 0.78)",
          border: "1px solid",
          borderColor: "border.default",
          opacity: 0,
          transition: "opacity 120ms",
          // Stay above the SVG so click events hit the buttons.
          zIndex: 1,
        }}
      >
        <Tooltip title={copied ? "Copied!" : "Copy mermaid source"} arrow>
          <Box
            component="button"
            type="button"
            onClick={_handleCopySource}
            disabled={rendering}
            aria-label="Copy mermaid source"
            sx={{
              ..._iconButtonSx,
              color: copied ? "success.light" : "text.secondary",
            }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 16 }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            )}
          </Box>
        </Tooltip>

        <Tooltip title="Download as SVG" arrow>
          <Box
            component="button"
            type="button"
            onClick={_handleDownloadSvg}
            disabled={rendering}
            aria-label="Download as SVG"
            sx={_iconButtonSx}
          >
            <DownloadIcon sx={{ fontSize: 16 }} />
            <Box
              component="span"
              sx={{ fontSize: "0.65rem", fontWeight: 600, ml: 0.25 }}
            >
              SVG
            </Box>
          </Box>
        </Tooltip>

        <Tooltip title="Download as PNG (2× scale)" arrow>
          <Box
            component="button"
            type="button"
            onClick={_handleDownloadPng}
            disabled={rendering}
            aria-label="Download as PNG"
            sx={_iconButtonSx}
          >
            <DownloadIcon sx={{ fontSize: 16 }} />
            <Box
              component="span"
              sx={{ fontSize: "0.65rem", fontWeight: 600, ml: 0.25 }}
            >
              PNG
            </Box>
          </Box>
        </Tooltip>

        <Tooltip title="Fullscreen — zoom + pan" arrow>
          <Box
            component="button"
            type="button"
            onClick={() => setFullscreenOpen(true)}
            disabled={rendering || !svgMarkup}
            aria-label="Open fullscreen view"
            sx={_iconButtonSx}
          >
            <FullscreenIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      </Box>

      <FullscreenDiagram
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        svgMarkup={svgMarkup}
      />
    </Box>
  );
}

// Shared `sx` for the toolbar buttons. Reset the native <button> chrome
// (border, background, padding) and swap to our themed hover state.
const _iconButtonSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.25,
  px: 0.75,
  py: 0.5,
  border: "none",
  background: "transparent",
  color: "text.secondary",
  borderRadius: 1,
  cursor: "pointer",
  font: "inherit",
  transition: "background 120ms, color 120ms",
  "&:hover:not(:disabled)": {
    background: "rgba(255, 255, 255, 0.06)",
    color: "text.primary",
  },
  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
  },
};
